import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export default function useSimpleLogin() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [showForgotUsernameModal, setShowForgotUsernameModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [showLoggedOut, setShowLoggedOut] = useState(false);

  // Sign-in-with-code: an alternative to password login, not 2FA on top of it.
  const RESEND_COOLDOWN_SECONDS = 60;
  const MAX_OTP_ATTEMPTS = 3;
  const [authMethod, setAuthMethod] = useState('password'); // 'password' | 'otp'
  const [otpStep, setOtpStep] = useState('request'); // 'request' | 'verify'
  const [otpCode, setOtpCode] = useState('');
  const [otpRequestLoading, setOtpRequestLoading] = useState(false);
  const [otpInfoMessage, setOtpInfoMessage] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpAttempts, setOtpAttempts] = useState(0);

  // Tick the resend cooldown down once a second while on the code-entry step.
  useEffect(() => {
    if (otpStep !== 'verify' || resendCooldown <= 0) return undefined;
    const intervalId = setInterval(() => {
      setResendCooldown(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [otpStep, resendCooldown]);

  const { login, requestOtp, loginWithOtp, markPasswordChanged, user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if user is already authenticated
  useEffect(() => {
    // Wait for auth to finish loading before checking
    if (authLoading) {
      return;
    }

    // Check if user is authenticated (either from context or localStorage)
    const token = localStorage.getItem('faithinsight_token');
    const savedUser = localStorage.getItem('faithinsight_user');
    const sessionExpired = localStorage.getItem('faithinsight_session_expired');

    // Don't redirect if session expired or logged out
    if (sessionExpired === 'true') {
      return;
    }

    // If we have a token and user data, or if auth context says we're authenticated
    if ((token && savedUser) || (isAuthenticated && user)) {
      // Get redirect URL from query parameters or localStorage
      const urlParams = new URLSearchParams(location.search);
      const redirectFromQuery = urlParams.get('redirect');
      const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');

      // Store redirect in localStorage if it's in the URL
      if (redirectFromQuery && !redirectFromStorage) {
        localStorage.setItem('faithinsight_login_redirect', redirectFromQuery);
      }

      const redirectUrl = redirectFromQuery || redirectFromStorage || '/dashboard';

      // Clear redirect from localStorage if it was used
      if (redirectFromStorage && redirectFromQuery) {
        localStorage.removeItem('faithinsight_login_redirect');
      }

      // Navigate to the redirect URL
      if (redirectUrl === '/catalog') {
        window.location.href = '/portal/catalog?redirect=/catalog';
      } else {
        navigate(redirectUrl, { replace: true });
      }
    }
  }, [authLoading, isAuthenticated, user, location.search, navigate]);

  // Check for session expired or logged out flags on component mount
  useEffect(() => {
    const loggedOut = localStorage.getItem('faithinsight_logged_out');
    const sessionExpired = localStorage.getItem('faithinsight_session_expired');

    if (loggedOut === 'true') {
      setShowLoggedOut(true);
      // Clear the flag since we're showing the message
      localStorage.removeItem('faithinsight_logged_out');
    } else if (sessionExpired === 'true') {
      setShowSessionExpired(true);
      // Clear the flag since we're showing the message
      localStorage.removeItem('faithinsight_session_expired');
    }
  }, []);

  // Preserve redirect parameter in localStorage when component mounts or URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const redirectFromQuery = urlParams.get('redirect');
    const redirectFromState = location.state?.from?.pathname;

    // Store redirect in localStorage so it persists through errors and password reset flows
    const redirectToStore = redirectFromQuery || redirectFromState;
    if (redirectToStore) {
      localStorage.setItem('faithinsight_login_redirect', redirectToStore);
    }
  }, [location.search, location.state]);

  const getRedirectUrl = (clearAfterUse = false) => {
    // Check for redirect in query parameters first
    const urlParams = new URLSearchParams(location.search);
    const redirectFromQuery = urlParams.get('redirect');

    // Then check for redirect in location state
    const redirectFromState = location.state?.from?.pathname;

    // Then check localStorage (preserved from previous visits)
    const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');

    // Prioritize query parameter, then state, then localStorage, then default to dashboard
    const redirectUrl = redirectFromQuery || redirectFromState || redirectFromStorage || '/dashboard';

    // Only clear from localStorage after successful login (when clearAfterUse is true)
    if (clearAfterUse && redirectFromStorage) {
      localStorage.removeItem('faithinsight_login_redirect');
    }

    return redirectUrl;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  // Shared by every successful-login path (password, OTP, post-password-change):
  // resolve the redirect target and navigate, with the catalog special-case
  // (full page reload to avoid a dashboard flash).
  const navigateAfterLogin = () => {
    const redirectUrl = getRedirectUrl(false);

    if (redirectUrl === '/catalog') {
      window.location.href = '/portal/catalog?redirect=/catalog';
    } else {
      const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');
      if (redirectFromStorage) {
        localStorage.removeItem('faithinsight_login_redirect');
      }
      navigate(redirectUrl, { replace: true });
    }
  };

  // Shared by both sign-in methods: branch on requiresPasswordChange, else navigate.
  const completeLogin = (result) => {
    if (result.requiresPasswordChange) {
      setShowPasswordChangeModal(true);
    } else {
      navigateAfterLogin();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Clear logged out and session expired messages when user attempts to log in
    setShowLoggedOut(false);
    setShowSessionExpired(false);

    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    if (!formData.password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);
    const result = await login(formData.email, formData.password);

    if (result.success) {
      completeLogin(result);
    } else {
      setError(result.error || 'Login failed. Please try again.');
      // Don't clear redirect on error - keep it for next login attempt
    }

    setLoading(false);
  };

  const handlePasswordChangeSuccess = () => {
    markPasswordChanged();
    setShowPasswordChangeModal(false);
    navigateAfterLogin();
  };

  const handleToggleAuthMethod = (method) => {
    setAuthMethod(method);
    setOtpStep('request');
    setOtpCode('');
    setOtpInfoMessage('');
    setResendCooldown(0);
    setOtpAttempts(0);
    setError('');
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError('');
    setShowLoggedOut(false);
    setShowSessionExpired(false);

    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    setOtpRequestLoading(true);
    await requestOtp(formData.email);
    setOtpStep('verify');
    setOtpCode('');
    setOtpAttempts(0);
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    setOtpRequestLoading(false);
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setOtpRequestLoading(true);
    await requestOtp(formData.email);
    // Enumeration-safe: same feedback regardless of whether the account
    // exists, is on cooldown server-side, or hit the hourly cap.
    setOtpInfoMessage('A new code has been sent.');
    setOtpCode('');
    setOtpAttempts(0);
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    setOtpRequestLoading(false);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');

    if (!otpCode || otpCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    const result = await loginWithOtp(formData.email, otpCode);

    if (result.success) {
      completeLogin(result);
      setLoading(false);
      return;
    }

    const attemptsUsed = otpAttempts + 1;
    setOtpAttempts(attemptsUsed);
    setOtpCode('');

    if (attemptsUsed >= MAX_OTP_ATTEMPTS) {
      // Give up on this code after 3 tries - back to the main sign-in screen
      // rather than leaving the user stuck retrying a code that's likely
      // dead (the server itself locks it out after 5 wrong attempts).
      setAuthMethod('password');
      setOtpStep('request');
      setOtpInfoMessage('');
      setOtpAttempts(0);
      setError('One-time code could not be verified. Please try again.');
    } else {
      const remaining = MAX_OTP_ATTEMPTS - attemptsUsed;
      setError(`Incorrect code. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`);
    }

    setLoading(false);
  };

  const handleNeedHelp = () => {
    // Open email client to admin
    window.open('mailto:admin@faithinsight.org');
  };

  return {
    formData,
    showPassword,
    setShowPassword,
    loading,
    error,
    showPasswordChangeModal,
    setShowPasswordChangeModal,
    showForgotUsernameModal,
    setShowForgotUsernameModal,
    showForgotPasswordModal,
    setShowForgotPasswordModal,
    showSessionExpired,
    showLoggedOut,
    location,
    navigate,
    handleChange,
    handleSubmit,
    handlePasswordChangeSuccess,
    handleNeedHelp,
    authMethod,
    otpStep,
    otpCode,
    setOtpCode,
    otpRequestLoading,
    otpInfoMessage,
    resendCooldown,
    handleToggleAuthMethod,
    handleRequestOtp,
    handleResendOtp,
    handleVerifyOtp
  };
}
